import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Button from './Button/Button';
import PropTypes from 'prop-types';
import Typography from './Typography';
import PriceDisplay from './PriceDisplay';
import { theme } from '../theme';
import { useTranslation } from 'react-i18next';
import { formatEventDateDisplay, isEventPast } from '../utils/dateUtils';
import ArrowRightIcon from './Icons/ArrowRightIcon';
import GearIcon from './Icons/GearIcon';
import { useCookieConsent } from '../context/CookieConsentContext';

const EventFixedAction = ({
  eventName,
  eventStartDate,
  availableSeats,
  tickets,
  isEnrolled,
  onEnroll,
  handleEnroll,
  onViewEnrollment,
  isOwner,
  isMobile,
  isUserLoggedIn,
  disabled,
  eventEndDate,
  attendeesCount,
  eventId
}) => {
  const { t, i18n } = useTranslation('eventDetail');
  const eventHasPassed = isEventPast(eventEndDate);
  const eventDate = formatEventDateDisplay(eventStartDate, eventEndDate, i18n.language);
  const barRef = useRef(null);
  const { setBottomOffset } = useCookieConsent();

  useEffect(() => {
    const el = barRef.current;
    if (!el || !setBottomOffset) return;

    const observer = new ResizeObserver(([entry]) => {
      setBottomOffset(entry.target.offsetHeight);
    });
    observer.observe(el);
    setBottomOffset(el.offsetHeight);

    return () => {
      observer.disconnect();
      setBottomOffset(0);
    };
  }, [setBottomOffset]);

  return (
    <EventFixedBar ref={barRef}>
      <Container>
        <EventHeading>
          <EventTitle $variant="title-3-semibold" $variantTablet="title-5-semibold" $variantMobile="title-5-semibold" as="h2">
            {eventName}
          </EventTitle>
          <Typography $variant="body-1-medium" $variantTablet="body-2-medium" $variantMobile="body-2-medium" as='p' color={theme.colors.defaultWeak}>
            {eventDate}
          </Typography>
        </EventHeading>

        <EventSeats>
          {!eventHasPassed ? (
            <>
              <PriceDisplay tickets={tickets} />
              <Typography $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold" as='p' color={theme.colors.defaultWeak}>
                {t('eventFixedActions.seatsAvailable', { count: availableSeats })}
              </Typography>
            </>
          ) : (
            <>
              <PriceDisplay tickets={tickets} />
              <Typography $variant="body-1-semibold" $variantTablet="body-2-semibold" $variantMobile="body-2-semibold" as='p' color={theme.colors.defaultWeak}>
                {t('eventFixedActions.attendeesCount', { count: attendeesCount })}
              </Typography>
            </>
          )}
        </EventSeats>

        <EventActions>
          {!eventHasPassed ? (
            isOwner ? (
              <Button
                onClick={() => window.location.href = `/events/manage/${eventId}`}
                $variant=""
                size="l"
                sizeTablet="l"
                sizeMobile="l"
                $fullWidth
              >
                {/* Gestionar evento */}
                <GearIcon height={16} width={16} fill={theme.colors.inverseMain} />
                {t('eventDetailHeader.manageEventButton')}
                {/* <ArrowRightIcon fill={theme.colors.inverseMain} width={16} height={16} /> */}
              </Button>
            ) : isEnrolled ? (
              <Button
                onClick={onViewEnrollment}
                $variant=""
                size="l"
                sizeTablet="l"
                sizeMobile="l"
                disabled={disabled}
                $fullWidth
              >
                {t('eventDetailHeader.viewEnrollment', { ns: 'eventDetail' })}
                <ArrowRightIcon fill={theme.colors.inverseMain} width={16} height={16} />
              </Button>
            ) : (
              <Button
                onClick={handleEnroll}
                $variant=""
                size="xl"
                sizeTablet="l"
                sizeMobile="l"
                disabled={disabled}
                $fullWidth
              >
                {t('eventFixedActions.enrollButton')}
              </Button>
            )
          ) : (
            <PastEventBadge>
              <Typography
                $variant="body-1-semibold"
                $variantTablet="body-2-semibold"
                $variantMobile="body-2-semibold"
                as="p"
                color={theme.colors.defaultStrong}
              >
                {t('eventFixedActions.eventFinished')}
              </Typography>
            </PastEventBadge>
          )}
        </EventActions>
      </Container>
    </EventFixedBar>
  );
};


EventFixedAction.propTypes = {
  eventName: PropTypes.string.isRequired,
  eventStartDate: PropTypes.string.isRequired,
  availableSeats: PropTypes.number.isRequired,
  tickets: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      price: PropTypes.number,
      availableSeats: PropTypes.number.isRequired,
    })
  ).isRequired,
  isEnrolled: PropTypes.bool,
  onEnroll: PropTypes.func,
  onViewEnrollment: PropTypes.func,
  eventEndDate: PropTypes.string.isRequired,
  attendeesCount: PropTypes.number.isRequired,
  eventId: PropTypes.string,
};

export default EventFixedAction;

//Estilos del componente

const EventFixedBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  box-shadow: 0px -2px 8px 0px rgba(26, 26, 26, 0.08);
  z-index: 100;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    border-top-left-radius: ${({ theme }) => theme.radius.sm};
    border-top-right-radius: ${({ theme }) => theme.radius.sm};
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  display: flex;
  flex-direction: row;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  align-items: center;
  gap: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm} 0px 0px 0px;
    flex-direction: column;
    gap: 0px;
  }
`;

const EventHeading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  flex: 1 0 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.xxs};
    padding-left: ${({ theme }) => theme.sizing.sm};
    padding-right: ${({ theme }) => theme.sizing.sm};
  }
`;

const EventSeats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm} 0px ${({ theme }) => theme.sizing.sm};
  }
`;

const EventActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};



  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: ${({ theme }) => theme.sizing.sm};
    // padding-top: ${({ theme }) => theme.sizing.xs};
  }
`;

const EventTitle = styled(Typography)`
  flex-shrink: 0;
`;

const PastEventBadge = styled.div`
  min-height: ${({ theme }) => theme.sizing.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.fill.defaultStrong};
  border-radius: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  width: 100%;
`;
